import React, { useState } from "react";

export const useElementHeightObserver = (selectors: string) => {
  const [positionBottom, setPositionBottom] = useState(0);

  React.useEffect(() => {
    const { disconnect } = observeElement(selectors, (element) => {
      if (element) setPositionBottom(element.clientHeight);
      else setPositionBottom(0);
    });

    return () => {
      disconnect();
    };
  }, [selectors]);

  return positionBottom;
};

const observeElement = (
  selectors: string,
  callback: (el: Element | undefined) => void
) => {
  let resizeObserver;

  try {
    // eslint-disable-next-line compat/compat
    resizeObserver = new ResizeObserver((entries) => {
      const keepBookingElement = entries[0].target;
      if (keepBookingElement) callback(keepBookingElement);
      else callback(undefined);
    });
  } catch (_e) {
    //not supported by browser, ignore resize observation
  }

  const mutationObserver = waitForAddedNode(selectors, (element) => {
    if (resizeObserver) {
      resizeObserver.observe(element);
    } else {
      //fallback in case resize observer is not supported
      callback(element);
    }
  });

  const disconnect = () => {
    if (resizeObserver) resizeObserver.disconnect();
    if (mutationObserver) mutationObserver.disconnect();
  };

  return { disconnect };
};

const waitForAddedNode = (
  selectors: string,
  callback: (el: Element) => void
): MutationObserver | undefined => {
  const exisingElement = document.querySelector(selectors);
  if (exisingElement) {
    callback(exisingElement);
    return undefined;
  }

  // if the element is not in the DOM yet, we need to wait for it
  const observer = new MutationObserver((_mutations, observer) => {
    const el = document.querySelector(selectors);
    if (el) {
      observer.disconnect();
      callback(el);
    }
  });
  observer.observe(document, {
    subtree: true,
    childList: true
  });

  return observer;
};
